import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SearchFilters from './components/SearchFilters';
import ItemsGrid from './components/ItemsGrid';
import { swapItems } from '../../mocks/swapItems';

export default function BrowsePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedCondition, setSelectedCondition] = useState('All Conditions');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');
  const [sortBy, setSortBy] = useState('distance');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredItems = swapItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All Categories' || item.category === selectedCategory;
    const matchesCondition = selectedCondition === 'All Conditions' || item.condition === selectedCondition;
    const matchesLocation = selectedLocation === 'All Locations' || item.location === selectedLocation;
    
    return matchesSearch && matchesCategory && matchesCondition && matchesLocation;
  }).sort((a, b) => {
    if (sortBy === 'distance') {
      return parseFloat(a.distance) - parseFloat(b.distance);
    } else if (sortBy === 'newest') {
      return b.id - a.id;
    } else if (sortBy === 'popular') {
      return b.interested - a.interested;
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isScrolled={isScrolled} />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Browse Items
            </h1>
            <p className="text-lg text-gray-600">
              Discover thousands of items available for swap in your area
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pr-14 text-base border-2 border-gray-300 rounded-xl focus:outline-none focus:border-teal-500 transition-colors bg-white"
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors">
                <i className="ri-search-line text-xl"></i>
              </button>
            </div>
          </div>

          <div className="flex gap-6">
            {/* Sidebar Filters */}
            <SearchFilters
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedCondition={selectedCondition}
              setSelectedCondition={setSelectedCondition}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              onClearFilters={() => {
                setSelectedCategory('All Categories');
                setSelectedCondition('All Conditions');
                setSelectedLocation('All Locations');
                setSearchQuery('');
              }}
            />

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-6 bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-600">
                    <strong className="text-gray-900">{filteredItems.length}</strong> items found
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
                        viewMode === 'grid' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-grid-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
                        viewMode === 'list' ? 'bg-white text-teal-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-list-check text-lg"></i>
                    </button>
                  </div>

                  {/* Sort Dropdown */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 cursor-pointer bg-white"
                  >
                    <option value="distance">Nearest First</option>
                    <option value="newest">Newest First</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>
              </div>

              {/* Items Display */}
              <ItemsGrid items={filteredItems} viewMode={viewMode} />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
